import {
  Box,
  IconButton,
  Modal,
  ModalProps,
  Stack,
  SxProps,
} from '@mui/material';
import { CSSProperties, FC, memo } from 'react';
import useDisclosure, { UseDisclosureInterface } from './useDisclosure';
import ContainerTitle from '@components/ui/container-title';
import { Close } from '@mui/icons-material';
import { getColor } from '@assets/theme';
import LoadingComponent from '@components/animation/loading-component';

/**
 * Interface for the Modal component props
 * @extends Omit<ModalProps, 'open'>
 */
export interface ModalInterface extends Omit<ModalProps, 'open'> {
  /** Disables the onClose function when set to true */
  disableOnClose?: boolean;
  /** Custom styles for the inner Box component */
  boxStyle?: CSSProperties | SxProps;
  /** Disclosure object for controlling the modal's open state */
  modalDisclosure: UseDisclosureInterface;
  withHeader?: {
    title: string;
    onClose: () => void;
    learnMore?: () => void;
  };
  loading?: boolean;
}

/**
 * A wrapper component for Material-UI Modal with additional functionality
 * @param {ModalInterface} props - The props for the ModalWrapper component
 * @returns {JSX.Element} The ModalWrapper component
 */
export const ModalWrapper: FC<ModalInterface> = memo(
  ({
    children,
    disableOnClose,
    boxStyle,
    modalDisclosure,
    withHeader,
    loading,
    ...props
  }) => {
    const { white, primary } = getColor();
    return (
      <Modal
        {...props}
        open={modalDisclosure.isOpen}
        onClose={disableOnClose || loading ? () => {} : modalDisclosure.onClose}
        sx={{ overflow: 'auto', ...props?.sx }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100dvh',
          }}
        >
          {withHeader ? (
            <Stack
              sx={{
                // flex: 1,
                // height: '100%',
                // overflow: 'hidden',
                // overflowY: 'auto',
                maxWidth: '90%',
                maxHeight: '90%',
                bgcolor: 'background.paper',
                borderRadius: '4px',
                boxShadow: 6,

                overflow: 'auto',
                minHeight: 0,
                position: 'relative',
                ...boxStyle,
              }}
            >
              {loading && <LoadingComponent />}
              {/* HEADER */}
              <Stack
                direction='row'
                gap={{ xs: 1, md: 2 }}
                sx={{
                  px: 3,
                  py: 2,
                  borderBottom: '1px solid #EEE',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  bgcolor: white,
                  borderRadius: '4px',
                }}
              >
                <ContainerTitle
                  title={withHeader.title}
                  func={withHeader?.learnMore || undefined}
                  titleStyle={{ fontSize: '20px!important' }}
                />
                <Box sx={{ flexGrow: 1 }} />
                <IconButton
                  id='modal-close-btn'
                  disableRipple
                  sx={[
                    {
                      border: 0,
                      bgcolor: 'transparent!important',
                      '&.MuiButtonBase-root': {
                        bgcolor: 'transparent!important',
                      },
                      ':hover': {
                        bgcolor: 'transparent',
                        color: primary,
                      },
                    },
                  ]}
                  onClick={withHeader.onClose}
                >
                  <Close />
                </IconButton>
              </Stack>

              {/* MAIN */}
              <Stack
                className='with-header-main'
                sx={{
                  // px: 3,
                  // py: 2,
                  p: 3,
                  gap: 2,
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                }}
              >
                {children}
              </Stack>
            </Stack>
          ) : (
            <Stack
              sx={{
                maxWidth: '90%',
                maxHeight: '90%',
                bgcolor: 'background.paper',
                borderRadius: '4px',
                boxShadow: 6,
                overflow: 'auto',
                minHeight: 0,
                position: 'relative',
                ...boxStyle,
              }}
            >
              {loading && <LoadingComponent />}
              {children}
            </Stack>
          )}
        </Box>
      </Modal>
    );
  },
);

/**
 * Custom hook for managing modal functionality
 * @returns {Object} An object containing the ModalWrapper component and modalDisclosure
 */
const useModal = () => {
  const modalDisclosure = useDisclosure();

  return { ModalWrapper, modalDisclosure };
};

export default useModal;
